// CoverMyClass — typed API client for Azure Functions backend
// Base URL is injected at build time via VITE_API_BASE_URL.
// Falls back to /api so Front Door routes it correctly in production.

import { isDemoMode } from './demo/store';
import { demoRequest } from './demo/api-mock';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Demo intercept — short-circuit before any network call. Every email,
  // upsert, and lookup is handled in-memory and any "outbound email" is
  // routed to the DemoInbox component.
  if (isDemoMode()) {
    return demoRequest<T>(path, init);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${init?.method ?? 'GET'} ${path} → ${res.status}: ${body}`);
  }
  // 204 No Content
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ── Emergency Alerts ───────────────────────────────────────────────

export interface SendEmergencyAlertPayload {
  alertId: string;
  coverTeacherId: string;
  coverTeacherName: string;
  classroom: string;
  subject: string;
  lessonId: string;
}

export interface EmergencyAlertStatusResponse {
  status: 'pending' | 'snoozed' | 'accepted' | 'cancelled';
  acceptedByName?: string;
  acceptedByTitle?: string;
  acceptedAt?: number;
}

export interface AcceptEmergencyAlertPayload {
  adminId: string;
  adminName: string;
  adminTitle: string; // "Mr" | "Mrs" | "Ms" | "Dr" | ""
}

/** POST /api/emergency-alerts — fires push notifications to all school admins.
 *  Requires the caller's own Bearer token — the backend derives schoolId
 *  from it rather than trusting a client-supplied value. */
export function sendEmergencyAlert(accessToken: string, payload: SendEmergencyAlertPayload): Promise<void> {
  return request('/emergency-alerts', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
}

/** GET /api/emergency-alerts/:id — poll for status updates */
export function getEmergencyAlertStatus(alertId: string): Promise<EmergencyAlertStatusResponse> {
  return request(`/emergency-alerts/${alertId}`);
}

/** POST /api/emergency-alerts/:id/accept — one admin accepts; broadcasts downgrade push to others */
export function acceptEmergencyAlert(
  alertId: string,
  payload: AcceptEmergencyAlertPayload,
): Promise<void> {
  return request(`/emergency-alerts/${alertId}/accept`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** POST /api/emergency-alerts/:id/cancel — cover teacher or admin cancels the alert */
export function cancelEmergencyAlert(alertId: string): Promise<void> {
  return request(`/emergency-alerts/${alertId}/cancel`, { method: 'POST' });
}

// ── Push Subscriptions ────────────────────────────────────────────

export interface SavePushSubscriptionPayload {
  userId: string;
  role: 'school' | 'teacher' | 'cover';
  subscription: PushSubscriptionJSON;
}

/** POST /api/push-subscriptions — registers a Web Push subscription endpoint.
 *  Requires the caller's own Bearer token — the backend derives schoolId
 *  from it rather than trusting a client-supplied value. */
export function savePushSubscription(accessToken: string, payload: SavePushSubscriptionPayload): Promise<void> {
  return request('/push-subscriptions', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
}

/** GET /api/push-config — returns the VAPID public key */
export function getPushConfig(): Promise<{ vapidPublicKey: string }> {
  return request('/push-config');
}

// ── Auth ─────────────────────────────────────────────────────────

/** POST /api/auth/register-school — creates a Supabase user for the school admin */
export function registerSchool(
  email: string,
  password: string,
  schoolName: string,
): Promise<{ userId: string; hashedToken?: string }> {
  return request('/auth/register-school', {
    method: 'POST',
    body: JSON.stringify({ email, password, schoolName }),
  });
}

/** POST /api/send-cover-notification — creates/updates cover teacher Supabase account and emails lesson details with credentials */
export function sendCoverNotification(payload: {
  toEmail: string;
  toName: string;
  subjectName: string;
  yearGroup: string;
  date: string;
  period: string;
  classroom: string;
  coveringFor: string;
  schoolName: string;
  schoolId?: string;
  lessonId?: string;
  /** Whether the recipient is a school admin or teaching staff — decides
   *  which portal the email link opens. Authoritative because the assigning
   *  admin knows exactly who they picked, regardless of Supabase account state. */
  recipientKind?: 'admin' | 'staff';
}, token?: string): Promise<void> {
  return request('/send-cover-notification', {
    method: 'POST',
    ...(token ? { headers: authHeaders(token) } : {}),
    body: JSON.stringify(payload),
  });
}

/** GET /api/cover-lessons — returns lessons assigned to the authenticated cover teacher (Bearer JWT) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getCoverLessons(accessToken: string): Promise<any[]> {
  return request('/cover-lessons', { headers: authHeaders(accessToken) });
}

/** POST /api/send-reset-link — generates a Supabase recovery link via admin API and emails it via Resend (no client rate limits) */
export function sendResetLink(email: string): Promise<void> {
  return request('/send-reset-link', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

// ── User Profiles ─────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  tutorialSeen: boolean;
  coverTutorialSeen: boolean;
  /** Optional: documents predating the field won't carry it. */
  schoolTutorialSeen?: boolean;
  /** Keys of every other tour this account has finished or skipped. */
  toursSeen?: string[];
  firstActivatedAt: number;
  lastLoginAt: number;
}

function authHeaders(accessToken: string) {
  return { Authorization: `Bearer ${accessToken}` };
}

/** GET /api/user-profile — fetches or creates the authenticated user's profile. Works for any role: the endpoint keys on the bearer's own user id. */
export function getUserProfile(accessToken: string): Promise<UserProfile> {
  return request('/user-profile', {
    headers: authHeaders(accessToken),
  });
}

/**
 * Retire several tours in ONE request.
 *
 * Exists because PATCH /user-profile is a read-modify-write with no
 * concurrency control: two un-awaited PATCHes fired back-to-back both read the
 * pre-update document and the second overwrites the first, so one tour silently
 * comes back on the next login. The cover-tour exits retire cover AND teacher
 * together, which is exactly that shape — so they send one request.
 */
export function markTutorialsSeen(
  accessToken: string,
  flags: { tutorialSeen?: boolean; coverTutorialSeen?: boolean; schoolTutorialSeen?: boolean },
): Promise<void> {
  return request('/user-profile', {
    method: 'PATCH',
    headers: { ...authHeaders(accessToken), 'Content-Type': 'application/json' },
    body: JSON.stringify(flags),
  });
}

/**
 * Record that this ACCOUNT has now seen a tour, by key.
 *
 * Called on completion AND on skip: skipping is still "I have been offered
 * this", and re-offering it on the next device is the behaviour being fixed.
 * Replaying stays manual and deliberately does NOT clear the flag.
 */
export function markTourSeen(accessToken: string, tourKey: string): Promise<void> {
  return request('/user-profile', {
    method: 'PATCH',
    headers: { ...authHeaders(accessToken), 'Content-Type': 'application/json' },
    body: JSON.stringify({ tourSeen: tourKey }),
  });
}

/** PATCH /api/user-profile — marks tutorial as seen for the authenticated user */
export function markTutorialSeen(accessToken: string, type: 'teacher' | 'cover' | 'school'): Promise<void> {
  const body = type === 'teacher'
    ? { tutorialSeen: true }
    : type === 'cover'
      ? { coverTutorialSeen: true }
      : { schoolTutorialSeen: true };
  return request('/user-profile', {
    method: 'PATCH',
    headers: authHeaders(accessToken),
    body: JSON.stringify(body),
  });
}

export interface ActivatedTeacher {
  email: string;
  activatedAt: string; // ISO timestamp from Supabase email_confirmed_at
}

/** GET /api/staff-status — returns confirmed teachers for the caller's own school */
export function getStaffActivationStatus(accessToken: string): Promise<{ activatedTeachers: ActivatedTeacher[] }> {
  return request('/staff-status', { headers: authHeaders(accessToken) });
}

// ── School Config ─────────────────────────────────────────────────

export interface SchoolConfig {
  name: string;
  subjects: string[];
  yearGroups: string[];
  rooms: string[];
  periods: { number: string; startTime: string; endTime: string }[];
  primaryColor: string;
  adminEmail?: string;
  postcode?: string;
  address?: string;
  urn?: string;
  dfeNumber?: string;
  logoImage?: string | null;
  departments?: string[];
  policies?: {
    behaviourPolicy?: { fileName?: string; keyPoints?: string; blobPath?: string };
    safeguardingPolicy?: { fileName?: string; keyPoints?: string; blobPath?: string };
    coverPolicy?: { fileName?: string; keyPoints?: string; blobPath?: string };
    additionalPolicies?: { type: string; name?: string; fileName?: string; keyPoints?: string; url?: string; blobPath?: string }[];
  };
  contacts?: {
    itSupport?: { name: string; email: string; phone: string };
    coverCoordinator?: { name: string; email: string; phone: string };
    headTeacher?: { name: string; email: string; phone: string };
    safeguarding?: { name: string; email: string; phone: string };
  };
  secondaryContacts?: { name: string; email: string; role: string; phone?: string }[];
  coverTeacherRules?: string[];
  guestAccount?: { username: string; password: string; notes: string };
  staff?: {
    id: string; firstName: string; lastName: string; email: string; department: string;
    phone?: string; subjects?: string[]; inviteCode: string; inviteSent: boolean;
    status?: 'pending' | 'activated' | 'terminated';
    invitedAt?: number; activatedAt?: number; lastLoginAt?: number;
  }[];
  /** Rotation length. 1 = single-week, 2+ = rotating multi-week timetable. No hard cap. */
  weekPattern?: number;
  /** Per-week period lists, length = weekPattern. weekPeriods[0] mirrors `periods` (Week 1). */
  weekPeriods?: { number: string; startTime: string; endTime: string }[][];
  /** ISO Monday anchoring Week 1 of the rotation. */
  weekAnchorDate?: string;
  /**
   * Annual retention review for archived cover observations (Stage 2 lifecycle).
   * The school is the data controller; this is the retention period it agrees.
   * Defaults are applied where undefined (see `resolveRetentionPolicy`).
   */
  observationRetention?: {
    /** Master switch for the annual delete cycle. Off = keep indefinitely (the
     *  school's own risk). Default on. */
    enabled?: boolean;
    /** Month (1-12) the annual review runs. Default 9 (September). */
    reviewMonth?: number;
    /** Day of month (1-31) the review runs. Default 1. */
    reviewDay?: number;
    /** Tighter opt-in: require an explicit admin approval before anything is
     *  permanently deleted. Default false = auto-delete after the grace window. */
    requireApproval?: boolean;
    /** Days after the review date before archived items are permanently deleted,
     *  during which an admin can still restore. Default 30. */
    graceDays?: number;
  };
}

/** Resolved retention policy with all defaults applied. Single source of truth
 *  for both the settings UI and (later) the annual review job. */
export interface ResolvedRetentionPolicy {
  enabled: boolean;
  reviewMonth: number;
  reviewDay: number;
  requireApproval: boolean;
  graceDays: number;
}

export const DEFAULT_RETENTION: ResolvedRetentionPolicy = {
  enabled: true,
  reviewMonth: 9,
  reviewDay: 1,
  requireApproval: false,
  graceDays: 30,
};

/** Fills in defaults for any unset retention field. */
export function resolveRetentionPolicy(
  r: SchoolConfig['observationRetention'] | undefined,
): ResolvedRetentionPolicy {
  return {
    enabled: r?.enabled ?? DEFAULT_RETENTION.enabled,
    reviewMonth: r?.reviewMonth ?? DEFAULT_RETENTION.reviewMonth,
    reviewDay: r?.reviewDay ?? DEFAULT_RETENTION.reviewDay,
    requireApproval: r?.requireApproval ?? DEFAULT_RETENTION.requireApproval,
    graceDays: r?.graceDays ?? DEFAULT_RETENTION.graceDays,
  };
}

/**
 * Reminder cadence for the retention grace window. Given the grace length in
 * days, returns the whole-day offsets (from the day the review fires) on which
 * to email the admin — proportionate to the length, and deliberately never
 * spammy. The annual-review job compares each day's offset-since-review against
 * this list to decide whether to send.
 *
 *   - Offset 0 is the opening warning ("archived data is due for permanent
 *     deletion — review or it auto-deletes on <date>").
 *   - The final offset (== graceDays) is the last notice, on the deletion day.
 *   - Longer windows get evenly-spaced intermediate reminders — roughly one per
 *     week of grace — capped at 5 total so a long window never becomes a nag.
 *
 * Examples:
 *    1 day  -> [0]                 (warning only; deletes next day)
 *    3 days -> [0, 3]              (warning + final)          ← user's example
 *    7 days -> [0, 4, 7]
 *   14 days -> [0, 5, 9, 14]
 *   30 days -> [0, 8, 15, 23, 30]  (the default)
 */
export function reminderOffsetsForGrace(graceDays: number): number[] {
  const n = Math.max(1, Math.floor(graceDays));
  if (n <= 1) return [0];
  // ~one email per week of grace, plus the opening warning and the final notice.
  const count = Math.min(5, Math.max(2, Math.floor(n / 7) + 2));
  const offsets: number[] = [];
  for (let i = 0; i < count; i++) offsets.push(Math.round((i * n) / (count - 1)));
  return Array.from(new Set(offsets)); // dedupe if rounding collides on small n
}

// ── Retention review (Stage 2 bulk review screen) ─────────────────────

export interface RetentionReviewClass { classId: string; displayLabel: string; archivedCount: number }
export interface RetentionReviewStatus {
  policy: ResolvedRetentionPolicy;
  totalArchived: number;
  classes: RetentionReviewClass[];
  cycleActive: boolean;
  reviewDateISO: string | null;
  deletionDateISO: string | null;
  daysUntilDeletion: number | null;
  deletedAt: number | null;
  /** Whether permanent deletion (auto + manual) is enabled for the org yet. */
  deletionEnabled: boolean;
}

/** GET /api/retention-review — status for the admin's school (powers the bulk review screen). */
export function getRetentionReviewStatus(accessToken: string): Promise<RetentionReviewStatus> {
  return request('/retention-review', { headers: authHeaders(accessToken) });
}

/** POST /api/retention-review/approve — admin approves permanent deletion of all still-archived observations. */
export function approveRetentionDeletion(accessToken: string): Promise<{ deleted: number; perClass: { classId: string; displayLabel: string; count: number }[] }> {
  return request('/retention-review/approve', { method: 'POST', headers: authHeaders(accessToken) });
}

export interface RetentionRunResult { applicable: boolean; emailedOffset: number | null; deleted: number | null; dryRun: boolean }
/** POST /api/retention-review/run — admin runs the daily review check for their school on demand (for testing). */
export function runRetentionReview(accessToken: string): Promise<RetentionRunResult> {
  return request('/retention-review/run', { method: 'POST', headers: authHeaders(accessToken) });
}

/** GET /api/school-config?schoolId=X — public, returns school config for cross-device access */
export function getSchoolConfig(schoolId: string): Promise<SchoolConfig> {
  return request(`/school-config?schoolId=${encodeURIComponent(schoolId)}`);
}

/** POST /api/school-config — school admin saves config so teachers can fetch it cross-device */
export function saveSchoolConfig(accessToken: string, config: SchoolConfig): Promise<void> {
  return request('/school-config', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(config),
  });
}

// ── Timetable Scan ────────────────────────────────────────────────

export interface ParsedTimetable {
  numberOfPeriods: number;
  periodDuration: number | null;
  schoolStartTime: string | null;
  breakAfterPeriod: number | null;
  lunchAfterPeriod: number | null;
  periods: { name: string; startTime: string | null; endTime: string | null }[];
  breakTime: { startTime: string | null; endTime: string | null } | null;
  lunchTime: { startTime: string | null; endTime: string | null } | null;
  registration: {
    enabled: boolean;
    name: string;
    morningStartTime: string | null;
    morningDuration: number | null;
    afternoonEnabled: boolean;
    afternoonStartTime: string | null;
    afternoonDuration: number | null;
  } | null;
  /** Subjects extracted from lesson cells */
  subjects?: string[];
  /** Room codes extracted from lesson cells */
  rooms?: string[];
  /** When the upload contains multiple weeks (Week 1/2, Week A/B), each
   *  additional week is returned here in order. Week 1 stays in the top-level
   *  fields. subjects[] and rooms[] are shared and not repeated per-week. */
  additionalWeeks?: ParsedWeek[];
  /** Computed: lunch duration in minutes (post-processed on the backend). */
  lunchDurationMins?: number;
  /** Computed: break duration in minutes (post-processed on the backend). */
  breakDurationMins?: number;
}

/** Shape of an entry in `ParsedTimetable.additionalWeeks` — Week 2+ of a rotation. */
export interface ParsedWeek {
  label?: string;
  numberOfPeriods?: number | null;
  periodDuration?: number | null;
  schoolStartTime?: string | null;
  breakAfterPeriod?: number | null;
  lunchAfterPeriod?: number | null;
  periods?: { name: string; startTime: string | null; endTime: string | null }[];
  breakTime?: { startTime: string | null; endTime: string | null } | null;
  lunchTime?: { startTime: string | null; endTime: string | null } | null;
  lunchDurationMins?: number;
  breakDurationMins?: number;
  registration?: {
    enabled?: boolean;
    name?: string;
    morningStartTime?: string | null;
    morningDuration?: number | null;
    afternoonEnabled?: boolean;
    afternoonStartTime?: string | null;
    afternoonDuration?: number | null;
  } | null;
}

/**
 * POST /api/parse-timetable
 * Sends a base64-encoded image or PDF to the backend, which uses Claude vision
 * to extract the school's period schedule. No auth required (called during onboarding).
 */
export function parseSchoolTimetable(
  imageData: string,
  mimeType: string,
): Promise<ParsedTimetable> {
  return request('/parse-timetable', {
    method: 'POST',
    body: JSON.stringify({ imageData, mimeType }),
  });
}

// ── AI block assist (Phase 2e.4) ────────────────────────────────────

/** Class profile context forwarded to the AI assist endpoint. Mirrors
 *  the continuity pillars on ClassEntity but flat — the backend
 *  assembles them into a prompt-friendly block. */
export interface AiBlockAssistClassContext {
  yearGroup?: string;
  subject?: string | null;
  setIdentifier?: string | null;
  examBoard?: string;
  specCode?: string;
  tier?: string;
  currentUnit?: string;
  pacing?: string;
  behaviourNorm?: string;
  entryRoutine?: string;
  pedagogyNorm?: string;
  equipment?: string[];
  customEquipment?: string[];
  calculatorPolicy?: string;
  targetLanguageRatio?: string;
  labSafetyBriefing?: boolean;
  kitPolicy?: string;
  otherDistinctives?: string;
}

export interface AiBlockAssistRequest {
  mode: 'ghost' | 'polish';
  kind: string;
  kindLabel: string;
  kindHint?: string;
  title: string;
  detail: string;
  classProfile?: AiBlockAssistClassContext;
}

/** Response shape from /api/ai-block-assist. Structured polish output
 *  separates the polished title (plain text, goes into the title
 *  input) from the polished detail (HTML, goes into the rich-text
 *  body). `suggestion` is kept for back-compat and mirrors `detail`. */
export interface AiBlockAssistResponse {
  /** Polished title — plain text, lands in the block's title input. */
  title?: string;
  /** Polished detail — HTML, lands in the block's rich-text body. */
  detail?: string;
  /** @deprecated Use `detail`. Kept so older deploys still work. */
  suggestion?: string;
}

/**
 * POST /api/ai-block-assist
 * Returns a structured polished version of the teacher's draft —
 * polished title separately from polished detail, so the title lands
 * in the title field and the detail lands in the body. Informed by
 * the class continuity profile for context.
 *
 * In demo mode the backend mock returns undefined; callers should
 * handle that and just hide the polish UI gracefully.
 */
export function aiBlockAssist(payload: AiBlockAssistRequest, accessToken?: string): Promise<AiBlockAssistResponse | undefined> {
  return request('/ai-block-assist', {
    method: 'POST',
    headers: accessToken ? authHeaders(accessToken) : undefined,
    body: JSON.stringify(payload),
  });
}

/**
 * POST /api/retrieval-request-notify
 * Tell the backend to email the school admin that a teacher requested retrieval
 * of an archived observation. Recipient is derived server-side; no pupil content
 * is sent. Best-effort — the request flag is already persisted on the class.
 */
export function notifyRetrievalRequest(
  payload: { classLabel: string; teacherName?: string; classId?: string },
  accessToken?: string,
): Promise<{ sent?: boolean } | undefined> {
  return request('/retrieval-request-notify', {
    method: 'POST',
    headers: accessToken ? authHeaders(accessToken) : undefined,
    body: JSON.stringify(payload),
  });
}

// ── Admin AI usage (real per-teacher token accounting) ───────────────

export interface AiUsageTeacherRow {
  teacherId: string;
  teacherEmail: string;
  teacherName: string;
  callCount: number;
  inputTokens: number;
  outputTokens: number;
  costGbp: number;
}

export interface AiUsageResponse {
  yearMonth: string;
  teachers: AiUsageTeacherRow[];
  totals: { callCount: number; costGbp: number };
  costBasis: {
    inputTokenGbp: number;
    outputTokenGbp: number;
    currency: 'GBP';
    model: string;
  };
}

export function getAiUsage(accessToken: string, yearMonth?: string): Promise<AiUsageResponse> {
  const qs = yearMonth ? `?yearMonth=${encodeURIComponent(yearMonth)}` : '';
  return request<AiUsageResponse>(`/ai-usage${qs}`, {
    headers: authHeaders(accessToken),
  });
}

// ── Lessons ───────────────────────────────────────────────────────

/** GET /api/lessons — returns lessons for the authenticated user's role (all school or teacher-only) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getLessons(accessToken: string): Promise<any[]> {
  return request('/lessons', { headers: authHeaders(accessToken) });
}

/** POST /api/lessons — upserts a lesson to Cosmos for cross-device sharing */
export function upsertLesson(accessToken: string, lesson: object): Promise<void> {
  return request('/lessons', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(lesson),
  });
}

/** POST /api/lesson-feedback — cover teacher submits feedback (no auth required).
 *  Phase 2f: optional `suggestions` array carries PillarSuggestion drafts that
 *  the backend writes alongside the feedback doc. The cover teacher's wizard
 *  produces both in one go; the backend persists them atomically so the
 *  teacher's review banner never sees an orphaned suggestion. */
export function submitLessonFeedback(
  lessonId: string,
  schoolId: string,
  feedback: object,
  lesson?: object,
  suggestions?: object[],
): Promise<void> {
  return request('/lesson-feedback', {
    method: 'POST',
    body: JSON.stringify({ lessonId, schoolId, feedback, lesson, suggestions }),
  });
}

// ── Pillar suggestions (cover-feedback → class profile review queue) ─

/** GET /api/pillar-suggestions?classId=X — pending suggestions for one class. */
export function getPillarSuggestions(accessToken: string, classId: string): Promise<unknown[]> {
  const qs = new URLSearchParams({ classId }).toString();
  return request<unknown[]>(`/pillar-suggestions?${qs}`, {
    headers: authHeaders(accessToken),
  });
}

/** PATCH /api/pillar-suggestions/:id — apply or discard a suggestion. */
export function resolvePillarSuggestion(
  accessToken: string,
  suggestionId: string,
  status: 'applied' | 'discarded',
): Promise<void> {
  return request(`/pillar-suggestions/${suggestionId}`, {
    method: 'PATCH',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ status }),
  });
}

/** PATCH /api/lessons/:id — updates lesson fields (status, rejectionReason, approvedAt, etc.) */
export function updateLessonStatus(accessToken: string, lessonId: string, update: object): Promise<void> {
  return request(`/lessons/${lessonId}`, {
    method: 'PATCH',
    headers: authHeaders(accessToken),
    body: JSON.stringify(update),
  });
}

export function deleteLessonFromCosmos(accessToken: string, lessonId: string): Promise<void> {
  return request(`/lessons/${lessonId}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
}

// ── Direct-to-blob file upload ────────────────────────────────────
// The backend issues a short-lived SAS URL; the browser PUTs the file
// straight to Blob Storage. Files never traverse the function — that's
// what lets us handle 100MB+ attachments without blowing the Cosmos
// 2MB doc limit or the host-to-worker gRPC frame size.

export interface FileUploadUrlResponse {
  /** Canonical blob path; the value we persist on the lesson step. */
  blobPath: string;
  /** Pre-signed PUT URL — short-lived, write-only. */
  uploadUrl: string;
  /** Epoch ms; the URL is unusable after this. */
  expiresAt: number;
}

export function getFileUploadUrl(
  accessToken: string,
  payload: { fileName: string; contentType: string; size: number; lessonId?: string; stepId?: string },
): Promise<FileUploadUrlResponse> {
  return request<FileUploadUrlResponse>('/files/upload-url', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
}

export interface FileReadUrlResponse {
  readUrl: string;
  expiresAt: number;
}

export function getFileReadUrl(
  accessToken: string,
  payload: { blobPath: string; fileName?: string },
): Promise<FileReadUrlResponse> {
  return request<FileReadUrlResponse>('/files/read-url', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
}

/** Upload a File directly to blob storage using a write-SAS URL. The
 *  caller already has the URL from `getFileUploadUrl`. Returns when
 *  the PUT completes; throws with the response body on non-2xx.
 *  Honors the demo-only `demo://<encoded blobPath>` scheme by caching
 *  the file as a data URL in localStorage so the demo flow never has
 *  to talk to Azure. */
export async function uploadFileToBlob(uploadUrl: string, file: File, contentType: string): Promise<void> {
  if (uploadUrl.startsWith('demo://')) {
    const blobPath = decodeURIComponent(uploadUrl.slice('demo://'.length));
    const dataUrl: string = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    });
    try {
      localStorage.setItem(`cmc_demo_blob:${blobPath}`, dataUrl);
    } catch (err) {
      // Quota likely — fall back to in-memory cache via window.
      const w = window as unknown as { __cmcDemoBlobs?: Record<string, string> };
      w.__cmcDemoBlobs = w.__cmcDemoBlobs ?? {};
      w.__cmcDemoBlobs[blobPath] = dataUrl;
      console.warn('[demo blob] localStorage quota, using in-memory store', err);
    }
    return;
  }
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': contentType,
    },
    body: file,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Blob upload failed (${res.status}): ${text || res.statusText}`);
  }
}

/** Upload a school policy document (PDF) to blob storage and return the
 *  blobPath to persist on school.policies. Uses the standard SAS flow
 *  with lessonId='policies' so paths group under
 *  <schoolId>/policies/<uid>-<name>. */
export async function uploadPolicyDocument(accessToken: string, file: File): Promise<string> {
  const { blobPath, uploadUrl } = await getFileUploadUrl(accessToken, {
    fileName: file.name,
    contentType: file.type || 'application/pdf',
    size: file.size,
    lessonId: 'policies',
  });
  await uploadFileToBlob(uploadUrl, file, file.type || 'application/pdf');
  return blobPath;
}

// ── Policy chat (cover-lesson policy assistant) ──────────────────
export interface PolicyChatCitation {
  title: string;
  pageStart: number;
  pageEnd: number;
  quote: string;
}
export interface PolicyChatResponse {
  grounded: boolean;
  answer?: string;
  citations: PolicyChatCitation[];
  docsAvailable: number;
  docTitles?: string[];
  skippedDocs?: string[];
  /** Demo only: questions left before the assistant stops answering. */
  remaining?: number;
  /** Demo only: the cap has been reached and no answer was produced. */
  limitReached?: boolean;
}

/** POST /api/policy-chat — grounded Q&A over the school's uploaded
 *  policy documents. Auth: Bearer token, or schoolId+coverCode for
 *  external cover teachers. */
export function policyChat(
  payload: {
    question: string;
    schoolId?: string;
    coverCode?: string;
    history?: { role: 'user' | 'assistant'; content: string }[];
  },
  accessToken?: string,
): Promise<PolicyChatResponse> {
  return request<PolicyChatResponse>('/policy-chat', {
    method: 'POST',
    body: JSON.stringify(payload),
    ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
  });
}

/** POST /api/invite-teacher — sends a Supabase invite email to a teacher */
export function inviteTeacher(
  email: string,
  firstName: string,
  lastName: string,
  schoolId: string,
  schoolName: string,
  accessToken?: string,
  subjects?: string[],
): Promise<void> {
  return request('/invite-teacher', {
    method: 'POST',
    body: JSON.stringify({ email, firstName, lastName, schoolId, schoolName, subjects }),
    ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
  });
}

/** POST /api/send-bulk-cover-notification — creates/updates cover teacher account and sends one email listing multiple lessons */
export function sendBulkCoverNotification(payload: {
  toEmail: string;
  toName: string;
  schoolName: string;
  schoolId?: string;
  lessons: Array<{
    lessonId: string;
    subject: string;
    yearGroup: string;
    date: string;
    period: string;
    classroom: string;
    coveringFor: string;
  }>;
  /** Whether the recipient is a school admin or teaching staff — decides
   *  which portal the email link opens. Authoritative because the assigning
   *  admin knows exactly who they picked, regardless of Supabase account state. */
  recipientKind?: 'admin' | 'staff';
}, token?: string): Promise<void> {
  return request('/send-bulk-cover-notification', {
    method: 'POST',
    ...(token ? { headers: authHeaders(token) } : {}),
    body: JSON.stringify(payload),
  });
}

/** POST /api/summarise-policies — generates AI cover teacher rules from
 *  policy key points and/or the uploaded policy PDFs themselves
 *  (`documents` with blobPaths — preferred; no typing needed). */
export function summarisePolicies(
  accessToken: string,
  payload: {
    schoolName: string;
    policies?: { name: string; keyPoints: string }[];
    documents?: { title: string; blobPath: string }[];
  },
): Promise<{ rules: string[] }> {
  return request('/summarise-policies', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
}

/** POST /api/notify-teacher — sends a termination notification email to a teacher */
export function notifyTeacherTermination(payload: {
  toEmail: string;
  toName: string;
  schoolName: string;
}): Promise<void> {
  return request('/notify-teacher', {
    method: 'POST',
    body: JSON.stringify({ ...payload, type: 'termination' }),
  });
}

export interface SchoolSuggestion {
  name: string;
  postcode: string;
  town: string;
  url: string;
}

/** GET /api/lookup-schools-by-postcode — school name search via allschools.co.uk (UK-wide) */
export async function lookupSchoolsByName(query: string): Promise<SchoolSuggestion[]> {
  try {
    const data = await request<{ schools: SchoolSuggestion[] }>(
      `/lookup-schools-by-postcode?q=${encodeURIComponent(query)}`,
    );
    return data.schools ?? [];
  } catch {
    return [];
  }
}

// ── Email OTP ─────────────────────────────────────────────────────

/** POST /api/auth/send-email-otp — generates a 6-digit OTP and emails it via Resend */
export function sendEmailOtp(email: string): Promise<void> {
  return request('/auth/send-email-otp', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/** POST /api/auth/verify-email-otp — verifies the submitted code against stored hash */
export function verifyEmailOtp(email: string, code: string): Promise<{ valid: boolean; error?: string }> {
  return request('/auth/verify-email-otp', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
}

/** POST /api/send-secondary-contact-invite — invites a secondary contact as
 *  school_admin via Supabase. Requires the caller's own Bearer token — the
 *  backend derives schoolId from it rather than trusting a client-supplied
 *  value. */
export function sendSecondaryContactInvite(accessToken: string, payload: {
  email: string;
  name: string;
  role: string;
  schoolName: string;
}): Promise<void> {
  return request('/send-secondary-contact-invite', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
}

// ── Classes (Phase 1 of cover-continuity redesign) ────────────────
//
// Each school maintains a list of teaching groups ("classes") that lessons
// can be linked to. The Class entity is what makes the per-class profile
// (built up from cover observations) possible — without a stable Class id,
// observations can't accrete to anything. Legacy lessons without a classId
// continue to work; this layer is additive.

import type { ClassEntity, PupilEntity } from './app-store';

/** GET /api/classes — all classes for the authenticated user's school. */
export function getClasses(accessToken: string): Promise<ClassEntity[]> {
  return request('/classes', { headers: authHeaders(accessToken) });
}

/** POST /api/classes — create or update a Class. Idempotent on `id`. */
export function upsertClass(accessToken: string, cls: ClassEntity): Promise<ClassEntity> {
  return request('/classes', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(cls),
  });
}

/** DELETE /api/classes/:id — soft-deletes (archives) a Class. */
export function archiveClass(accessToken: string, classId: string): Promise<void> {
  return request(`/classes/${classId}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
}

// ── Pupils (Phase 1) ──────────────────────────────────────────────
//
// Pupils are school-level entities (not class-level) so they can carry
// flags across multiple classes and across multiple academic years.

/** GET /api/pupils — all pupils for the authenticated user's school. */
export function getPupils(accessToken: string): Promise<PupilEntity[]> {
  return request('/pupils', { headers: authHeaders(accessToken) });
}

/** POST /api/pupils — create or update a Pupil. Idempotent on `id`. */
export function upsertPupil(accessToken: string, pupil: PupilEntity): Promise<PupilEntity> {
  return request('/pupils', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(pupil),
  });
}

/** DELETE /api/pupils/:id — soft-deletes (archives) a Pupil. */
export function archivePupil(accessToken: string, pupilId: string): Promise<void> {
  return request(`/pupils/${pupilId}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
}
